import { formatINR } from '../../../shared/utils/formatters';
import React, { useEffect, useState } from 'react';
import { Table, Button, Space, Tag, Modal, Form, InputNumber, Switch, message, Popconfirm, Typography } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { radiusPricingService, type RadiusPricingBand } from '../../../shared/api/services/radius-pricing.service';

const { Title, Text } = Typography;

export const RadiusPricingTable: React.FC = () => {
    const [bands, setBands] = useState<RadiusPricingBand[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingBand, setEditingBand] = useState<RadiusPricingBand | null>(null);
    const [form] = Form.useForm();

    const fetchBands = async () => {
        try {
            setLoading(true);
            const response = await radiusPricingService.getBands();
            if (response.success) {
                setBands(response.data);
            }
        } catch (error: any) {
            message.error(error.message || 'Failed to fetch pricing bands');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchBands();
    }, []);

    const handleAdd = () => {
        setEditingBand(null);
        // Find the max range of the last band to default the new min range
        const lastBand = bands.length > 0 ? bands[bands.length - 1] : null;
        const defaultMin = lastBand ? lastBand.max_range_km : 0;

        form.resetFields();
        form.setFieldsValue({
            min_range_km: defaultMin,
            is_active: true,
            currency: 'INR'
        });
        setIsModalVisible(true);
    };

    const handleEdit = (record: RadiusPricingBand) => {
        setEditingBand(record);
        form.setFieldsValue(record);
        setIsModalVisible(true);
    };

    const handleDelete = async (id: number) => {
        try {
            const response = await radiusPricingService.deleteBand(id);
            if (response.success) {
                message.success('Band deleted successfully');
                fetchBands();
            }
        } catch (error: any) {
            message.error(error.message || 'Failed to delete band');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            if (editingBand) {
                const response = await radiusPricingService.updateBand(editingBand.id, values);
                if (response.success) {
                    message.success('Band updated successfully');
                }
            } else {
                const response = await radiusPricingService.createBand(values);
                if (response.success) {
                    message.success('Band created successfully');
                }
            }
            setIsModalVisible(false);
            fetchBands();
        } catch (error: any) {
            if (error.errorFields) return;
            message.error(error.message || 'Failed to save band');
        }
    };

    const columns = [
        {
            title: 'Min Range (KM)',
            dataIndex: 'min_range_km',
            key: 'min_range_km',
            render: (val: any) => `${Number(val || 0)} km`,
        },
        {
            title: 'Max Range (KM)',
            dataIndex: 'max_range_km',
            key: 'max_range_km',
            render: (val: any) => val === null ? <Tag color="gold">∞ Infinity</Tag> : `${Number(val || 0)} km`,
        },
        {
            title: 'Rate per KM (₹)',
            dataIndex: 'price',
            key: 'price',
            render: (val: any) => formatINR(Number(val || 0).toFixed(2)),
        },
        {
            title: 'Status',
            dataIndex: 'is_active',
            key: 'is_active',
            render: (active: boolean) => (
                <Tag color={active ? 'green' : 'red'}>
                    {active ? 'ACTIVE' : 'INACTIVE'}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: RadiusPricingBand) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        size="small"
                    />
                    <Popconfirm
                        title="Delete pricing band?"
                        description="Are you sure you want to delete this band?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <div>
                    <Title level={4}>Radius Pricing Bands</Title>
                    <Text type="secondary">Define price ranges for extra distance radius upgrades</Text>
                </div>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAdd}
                >
                    Add Pricing Band
                </Button>
            </div>

            <Table
                columns={columns}
                dataSource={bands}
                rowKey="id"
                loading={loading}
                pagination={false}
            />

            <Modal
                title={editingBand ? 'Edit Pricing Band' : 'Add Pricing Band'}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                destroyOnHidden
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ is_active: true, currency: 'INR' }}
                >
                    <Form.Item
                        name="min_range_km"
                        label="Min Range (KM)"
                        rules={[{ required: true, message: 'Please enter minimum distance' }]}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                    </Form.Item>
                    <Form.Item
                        name="max_range_km"
                        label="Max Range (KM) - Leave empty for Infinity"
                        rules={[
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (value === null || value === undefined) {
                                        return Promise.resolve();
                                    }
                                    const min = getFieldValue('min_range_km');
                                    if (min < value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject(new Error('Max range must be greater than min range'));
                                },
                            }),
                        ]}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="Infinity" />
                    </Form.Item>
                    <Form.Item
                        name="price"
                        label="Rate per KM (₹)"
                        rules={[{ required: true, message: 'Please enter rate' }]}
                    >
                        <InputNumber style={{ width: '100%' }} min={0} precision={2} />
                    </Form.Item>
                    <Form.Item
                        name="is_active"
                        label="Active"
                        valuePropName="checked"
                    >
                        <Switch />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};
